import { type ChangeEvent, type ReactNode, useState } from "react";
import { Navigate, useNavigate } from "react-router";

import { ApiError, isError, isSuccess } from "@/api/ApiResult";
import { useCrud } from "@/api/useCrud";
import { CheckHash } from "@/components/check_hash/CheckHash";
import { Alert } from "@/components/ui/Alert/Alert";
import { FileInput } from "@/components/ui/FileInput/FileInput";
import { Form } from "@/components/ui/Form/Form";
import { FormLayout } from "@/components/ui/Form/FormLayout";
import { t, tx } from "@/i18n/translate";
import type {
  ELECTION_IMPORT_VALIDATE_REQUEST_PATH,
  ElectionDefinitionValidateResponse,
  NewElection,
} from "@/types/generated/openapi";
import { getDistrictNumber } from "@/utils/election";
import { fileTooLargeError, isFileTooLarge } from "@/utils/uploadFileSize";
import { useElectionCreateContext } from "../hooks/useElectionCreateContext";

function getSubtitle(election: NewElection) {
  if (election.sub_category === "PS2") {
    switch (election.committee_category) {
      case "GSB":
        if (election.district.district === "Specific") {
          return `${t("election.district")} ${getDistrictNumber(election.district)} (${election.district.name})`;
        }
        break;
      case "CSB":
        return t("election.all_districts");
    }
  }
  return undefined;
}

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: TODO function should be refactored
export function UploadCandidatesDefinition() {
  const { state, dispatch } = useElectionCreateContext();
  const navigate = useNavigate();
  const [error, setError] = useState<{ title: string; message: ReactNode } | undefined>();
  const [file, setFile] = useState<File | undefined>();
  const createPath: ELECTION_IMPORT_VALIDATE_REQUEST_PATH = `/api/elections/import/validate`;
  const { create } = useCrud<ElectionDefinitionValidateResponse>({ createPath });
  const defaultErrorTitle = t("election.invalid_candidates_definition.title");

  // if no election data was stored, navigate back to beginning
  if (!state.electionDefinitionData) {
    return <Navigate to="/elections/create" />;
  }

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO function should be refactored
  async function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    const currentFile = e.target.files ? e.target.files[0] : undefined;
    if (currentFile !== undefined) {
      setFile(currentFile);
      if (await isFileTooLarge(currentFile)) {
        setError({ title: defaultErrorTitle, message: fileTooLargeError(currentFile) });
        return;
      }

      const data = await currentFile.text();
      const response = await create({
        committee_category: state.committeeCategory,
        candidate_data: data,
        election_hash: state.electionDefinitionHash,
        election_data: state.electionDefinitionData,
        selected_region: state.gsbSelected,
      });

      if (isSuccess(response)) {
        dispatch({
          type: "SELECT_CANDIDATES_DEFINITION",
          response: response.data,
          candidateDefinitionData: data,
          candidateDefinitionFileName: currentFile.name,
        });
        setError(undefined);
      } else if (isError(response)) {
        // Response code 413 indicates that the file is too large
        if (response instanceof ApiError && response.code === 413) {
          setError({ title: defaultErrorTitle, message: fileTooLargeError(currentFile) });
        } else if (response instanceof ApiError && response.reference === "InvalidDistrict") {
          setError({
            title: t("election.wrong_candidates_definition.title"),
            message: tx("election.wrong_candidates_definition.description", {
              file: () => <strong>{currentFile.name}</strong>,
            }),
          });
        } else {
          setError({
            title: defaultErrorTitle,
            message: tx("election.invalid_candidates_definition.description", {
              file: () => <strong>{currentFile.name}</strong>,
            }),
          });
        }
      }
    } else {
      setFile(undefined);
    }
  }

  if (
    state.election &&
    state.candidateDefinitionFileName &&
    state.candidateDefinitionRedactedHash &&
    state.candidateDefinitionData &&
    !state.candidateDefinitionHash
  ) {
    async function onSubmit(chunks: string[]) {
      const response = await create({
        committee_category: state.committeeCategory,
        candidate_data: state.candidateDefinitionData,
        election_hash: state.electionDefinitionHash,
        election_data: state.electionDefinitionData,
        candidate_hash: chunks,
        selected_region: state.gsbSelected,
      });
      if (isSuccess(response)) {
        dispatch({
          type: "SET_CANDIDATES_DEFINITION_HASH",
          candidateDefinitionHash: chunks,
        });
        if (state.committeeCategory === "CSB") {
          await navigate("/elections/create/check-and-save");
        } else {
          await navigate("/elections/create/polling-stations");
        }
      } else if (isError(response) && response instanceof ApiError && response.reference === "InvalidHash") {
        setError({ title: defaultErrorTitle, message: response.message });
      }
    }

    return (
      <CheckHash
        title={`${state.election.election_id.split("_")[0]} ${state.election.domain?.name}`}
        subtitle={getSubtitle(state.election)}
        header={t("election.check_eml.candidates_title")}
        description={tx("election.check_eml.candidates_description", {
          file: () => {
            return <strong>{state.candidateDefinitionFileName}</strong>;
          },
        })}
        instructions={t("election.check_eml.check_hash_instructions")}
        errorTitle={t("election.check_eml.error.title")}
        errorDescription={t("election.check_eml.error.description")}
        redactedHash={state.candidateDefinitionRedactedHash}
        error={error ? error.message : undefined}
        onSubmit={(chunks) => void onSubmit(chunks)}
      />
    );
  }

  return (
    <section className="md">
      <Form title={t("election.import_candidates_eml")}>
        <FormLayout>
          <FormLayout.Section>
            {error && (
              <Alert type="error" title={error.title} inline>
                <p>{error.message}</p>
              </Alert>
            )}

            <p>{t("election.use_instructions_to_import_candidates_eml")}</p>
            <FileInput id="upload-eml" file={file} onChange={(e) => void onFileChange(e)}>
              {t("select_file")}
            </FileInput>
          </FormLayout.Section>
        </FormLayout>
      </Form>
    </section>
  );
}
